import {attach, combine, guard, sample} from 'effector';
import i18next from 'i18next';

import {toast} from 'react-toastify';

import {I18N_NS} from '../../core/constants/names';
import {DocumentProcessingStatus, DocumentStatus} from '../../core/dictionaries';
import {RestError, showRestError} from '../../core/errors';
import {CreateInvoiceGate} from '../../domain';
import {getAdapter} from '../../init';
import {DocumentsGridRawRowExtended, InvoiceTypeCodeStatus} from '../../types/invoices';

import {errorHandlerNoFirs} from '../create/helpers/errorHandlerNoFirs';

import {fetchDataFx, setInvoiceCreatingPending} from '../details';

import {
    fetchOptionFx,
    fetchCompanyNameFx,
    fetchTaxCategodyFx,
    fetchPaymentMeansFx,
    $invoiceState,
    deleteInvoiceFx,
    $invoiceStatusInfo,
    setNewInvoiceIsDone,
    $newInvoiceIsDone,
    updateInvoiceBodyFx,
    InvoiceStatusInfo,
    fetchDocumentListFx,
    fetchInvoiceStatusAcceptedFx,
    fetchInvoiceStatusRejectedFx,
    // fetchInvoicesFiltersFx,
} from './index';

deleteInvoiceFx.fail.watch(({error}) => showRestError(error as RestError));

const singleInvoiceFetchDataFx = attach({
    effect: fetchDocumentListFx,
    mapParams: () => ({
        sortings: [{id: 'createdAt', desc: true}],
        filters: [],
        paging: {
            pageNumber: 0,
            pageSize: 3,
        },
        search: $invoiceState.getState() ?? '',
        updOnly: true,
    }),
});

guard({
    clock: $invoiceState,
    filter: (i: string | null) => !!i && i !== 'new',
    target: singleInvoiceFetchDataFx,
});

sample({
    clock: CreateInvoiceGate.open,
    target: [fetchOptionFx, fetchCompanyNameFx, fetchTaxCategodyFx, fetchPaymentMeansFx],
});

fetchInvoiceStatusRejectedFx.fail.watch(({error}) => {
    errorHandlerNoFirs(error);
    return showRestError(error as RestError);
});
fetchInvoiceStatusAcceptedFx.fail.watch(({error}) => {
    errorHandlerNoFirs(error);
    return showRestError(error as RestError);
});
fetchInvoiceStatusRejectedFx.done.watch((reportId) => {
    toast.success(i18next.t('pages.documents.invoices.statusChanged', {reportId, ns: I18N_NS}), {
        className: 'Toastify__toast__wide',
    });
});
fetchInvoiceStatusAcceptedFx.done.watch((reportId) => {
    toast.success(i18next.t('pages.documents.invoices.statusChanged', {reportId, ns: I18N_NS}), {
        className: 'Toastify__toast__wide',
    });
});

$newInvoiceIsDone.on(setNewInvoiceIsDone, (_, data) => data);

sample({
    clock: [fetchInvoiceStatusRejectedFx.doneData, fetchInvoiceStatusAcceptedFx.doneData],
    fn: () => true,
    target: setNewInvoiceIsDone,
});

const invoiceStatusInfoGuard = guard({
    clock: $invoiceState,
    filter: (i: string | null) => !!i && i === 'new',
});
sample({
    clock: invoiceStatusInfoGuard,
    fn: () =>
        ({
            side: 'OUTGOING',
            status: DocumentStatus.NEW,
            processingStatus: DocumentProcessingStatus['CREATED'],
            docType: InvoiceTypeCodeStatus.BASIC,
        } as InvoiceStatusInfo),
    target: $invoiceStatusInfo,
});

$invoiceStatusInfo.on(fetchDataFx.doneData, (_, data) => {
    const {acceptStatus, direction, status, parentId, docType} = data;
    return {
        side: direction,
        status: acceptStatus as DocumentStatus,
        processingStatus: status as DocumentProcessingStatus,
        ...(parentId && {parentId}),
        ...(docType && {docType}),
    };
});

updateInvoiceBodyFx.fail.watch(({error}) => {
    errorHandlerNoFirs(error);
    return showRestError(error as RestError);
});

updateInvoiceBodyFx.done.watch((data: any) => {
    const {reportId} = data.result.data.data;
    toast.success(
        i18next.t('pages.documents.form.documentUpdated', {number: reportId, ns: I18N_NS}),
        {
            className: 'Toastify__toast__wide',
        },
    );

    setInvoiceCreatingPending(true);

    setNewInvoiceIsDone(true);
});
